/**
 * Lead Criteria presets — bundle default chips for different outbound personas.
 * Applying a preset fills targeting fields; geography, exclude titles, and options stay untouched.
 */
export type IcpPresetId = 'tech_executive' | 'literary_publishing' | 'sales_gtm' | 'custom';

export interface IcpPresetBundle {
  target_roles: string[];
  target_industries: string[];
  target_company_sizes: string[];
  target_seniorities: string[];
  target_funding_stages: string[];
  target_keywords: string[];
  target_technologies: string[];
  target_revenue_ranges: string[];
  target_departments: string[];
}

export const ICP_PRESET_META: Record<
  IcpPresetId,
  { label: string; description: string }
> = {
  tech_executive: {
    label: 'Tech / SaaS executive',
    description: 'CTO, CMO, VP, founder targets — tech-heavy industries and GTM stack signals',
  },
  literary_publishing: {
    label: 'Literary / publishing',
    description: 'Editors, agents, rights — publishing & media industries',
  },
  sales_gtm: {
    label: 'Sales / GTM leader',
    description: 'Revenue, sales, and growth leadership at B2B companies',
  },
  custom: {
    label: 'Custom',
    description: 'Choose your own chips — no template',
  },
};

export const ICP_PRESET_DEFAULTS: Record<Exclude<IcpPresetId, 'custom'>, IcpPresetBundle> = {
  tech_executive: {
    target_roles: [
      'CEO',
      'CTO',
      'CMO',
      'VP of Sales',
      'VP of Marketing',
      'Head of Growth',
      'Director of Engineering',
      'Product Manager',
      'Founder',
    ],
    target_industries: ['Technology', 'SaaS', 'Software'],
    target_company_sizes: ['11-50', '51-200', '201-500', '501-1000'],
    target_seniorities: ['C-Suite', 'VP', 'Director'],
    target_funding_stages: ['Seed', 'Series A', 'Series B', 'Series C'],
    target_keywords: [],
    target_technologies: ['Salesforce', 'HubSpot', 'AWS', 'Google Cloud', 'React'],
    target_revenue_ranges: ['1M-10M', '10M-50M', '50M-100M'],
    target_departments: ['Engineering', 'Product', 'Marketing', 'Sales'],
  },
  literary_publishing: {
    target_roles: [
      'Editor',
      'Literary Agent',
      'Acquisitions Editor',
      'Publisher',
      'Editorial Director',
      'Managing Editor',
      'Rights Director',
    ],
    target_industries: ['Publishing', 'Media', 'Education'],
    target_company_sizes: ['1-10', '11-50', '51-200', '201-500'],
    target_seniorities: ['VP', 'Director', 'Manager', 'Senior'],
    target_funding_stages: ['Pre-Seed', 'Seed', 'Series A'],
    target_keywords: ['publishing', 'books'],
    target_technologies: [],
    target_revenue_ranges: ['0-1M', '1M-10M', '10M-50M'],
    target_departments: ['Marketing', 'Operations'],
  },
  sales_gtm: {
    target_roles: [
      'VP of Sales',
      'Head of Growth',
      'Chief Revenue Officer',
      'Director of Sales',
      'CEO',
      'Founder',
      'CMO',
    ],
    target_industries: ['Technology', 'SaaS', 'Consulting', 'Marketing'],
    target_company_sizes: ['11-50', '51-200', '201-500', '501-1000'],
    target_seniorities: ['C-Suite', 'VP', 'Director'],
    target_funding_stages: ['Seed', 'Series A', 'Series B', 'Series C'],
    target_keywords: [],
    target_technologies: ['Salesforce', 'HubSpot', 'Slack', 'Zoom'],
    target_revenue_ranges: ['1M-10M', '10M-50M', '50M-100M'],
    target_departments: ['Sales', 'Marketing'],
  },
};

export function getIcpPresetBundle(id: IcpPresetId): IcpPresetBundle | null {
  if (id === 'custom') return null;
  return ICP_PRESET_DEFAULTS[id];
}
